import { memo, useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import {
  OFFER_TYPE_CRUISE,
} from 'constants/offer'
import { MINIMUM_RECOMMENDATION_OFFERS_TO_DISPLAY } from 'constants/recommendations'
import Pane from 'components/Common/Pane'
import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import useOfferList from 'hooks/Offers/useOfferList'
import useOffers from 'hooks/Offers/useOffers'
import { EmptyObject } from 'lib/object/objectUtils'
import { getOfferId } from 'lib/offer/offerUtils'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import VerticalSpacer from '../Spacing/VerticalSpacer'
import { CRUISE_FLASH_OFFERS_FILTERS } from 'constants/cruise'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import getOfferListKey from 'lib/offer/offerListKey'

const emptyFilters: App.OfferListFilters = EmptyObject

interface Props {
  filters?: App.OfferListFilters
  offer: App.CruiseOffer | App.Offer | App.TourOffer | App.VillaOffer
  cruiseLineName?: string
  departurePortName?: string
}

const RECOMMENDATION_TYPES: Array<App.OfferType> = [
  OFFER_TYPE_CRUISE,
]

function CruiseRecommendations(props: Props) {
  const {
    filters = emptyFilters,
    offer,
    cruiseLineName,
    departurePortName,
  } = props

  const [flashOfferIds, setFlashOfferIds] = useState<Array<string>>([])
  const [wideSearch, setWideSearch] = useState<boolean>(false)

  const offerId = useMemo(() => getOfferId(offer), [offer])

  const offerListOptions = useMemo(() => {
    return {
      excludedIds: [offerId],
    }
  }, [offerId])

  const offerList = useOfferList({
    ...filters,
    offerTypes: RECOMMENDATION_TYPES,
    // searching per cruise line can be a little restrictive at times
    // and we want to show a mininum number of offers
    // so if we didn't get any, widen our search to not restrict on cruise lines
    cruiseLines: wideSearch ? undefined : filters.cruiseLines,
  }, offerListOptions)

  /* Currently, cruise flash offer (tour v1) endpoint does not support the cruise filters
   * so we need to fetch the flash offers and filter them here
   * this will be removed when cruises stop using tours v1 to use the new cruise flash offer API
  */
  const flashOfferList = useOfferList(CRUISE_FLASH_OFFERS_FILTERS, offerListOptions)
  const [flashOffers] = useOffers<App.Offer>(flashOfferList.offerIds)

  useEffect(() => {
    const filteredOffers = flashOffers.filter(offer => (
      offer.id !== offerId &&
      offer.vendorName === cruiseLineName &&
      departurePortName?.includes(offer.startLocation || '')
    ))

    if (!flashOfferIds.length && filteredOffers.length) {
      setFlashOfferIds(filteredOffers.map(offer => offer.id))
    }
  }, [flashOffers, offerId, cruiseLineName, departurePortName, flashOfferIds])

  const finalOfferList = useMemo(() => {
    return {
      ...offerList,
      offerIds: [
        ...flashOfferIds,
        ...offerList.offerIds,
      ],
    }
  }, [offerList, flashOfferIds])

  const tracking = useOfferListTracking('Recommendations - Cruises', {
    ga: {
      category: 'Recommendations',
      label: 'Recommendations - Cruises',
    },
    key: getOfferListKey(filters),
  })

  useEffect(() => {
    if (!offerList.fetching && offerList.offerIds.length < MINIMUM_RECOMMENDATION_OFFERS_TO_DISPLAY) {
      setWideSearch(true)
    }
  }, [offerList])

  if (!offerList.offerIds.length) {
    return null
  }

  return <OfferListEventsProvider tracking={tracking}>
    <Pane padding="40 0">
      <VerticalSpacer gap={16}>
        <LayoutContainer>
          <Heading variant="heading3">
            <FormattedMessage
              id="QbIRvx3q3vYe_61SnwzK"
              defaultMessage="You may also like"
              
            />
          </Heading>
        </LayoutContainer>
        <OfferListCarousel overrideOfferList={finalOfferList} />
      </VerticalSpacer>
    </Pane>
  </OfferListEventsProvider>
}

export default memo(CruiseRecommendations)
